// src/components/store/sections/ai-recommender/index.tsx
"use client";

import React, { useRef, useState, useEffect } from "react";
import Link from "next/link";
import { Sparkles, ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import AiRecommenderDoorHanger from "./AiRecommenderDoorHanger";
import AiRecommenderFloatingCircle from "./AiRecommenderFloatingCircle";
import AiRecommenderCurvedCards from "./AiRecommenderCurvedCards";
import {
  PRODUCT_CARD_COMPONENTS,
  resolveProductCardComponent,
} from "../product-cards";
import { recordAiProductClick } from "../../ProductRecommendations";

export const AI_RECOMMENDER_VARIANTS: Record<string, React.ComponentType<any>> = {
  door_hanger: AiRecommenderDoorHanger,
  floating_circle: AiRecommenderFloatingCircle,
  curved_cards: AiRecommenderCurvedCards,
};

export interface AiRecommenderSectionProps {
  variant?: string;
  products?: any[];
  title?: string;
  subtitle?: string;
  primaryColor?: string;
  subdomain?: string;
}

export default function AiRecommenderSectionRenderer({
  variant = "door_hanger",
  products = [],
  title = "Recommended For You",
  subtitle = "AI-powered picks based on your preferences & trending items",
  primaryColor = "#059669",
  subdomain = "nextlook",
}: AiRecommenderSectionProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeVariant, setActiveVariant] = useState<string>(variant || "door_hanger");

  useEffect(() => {
    const syncDesign = () => {
      if (variant) setActiveVariant(variant);
      if (typeof window !== "undefined") {
        try {
          const rawSectionDesigns = localStorage.getItem("section_designs");
          if (rawSectionDesigns) {
            const parsed = JSON.parse(rawSectionDesigns);
            if (parsed.ai_recommender) {
              setActiveVariant(parsed.ai_recommender);
            }
          }
        } catch {}
      }
    };
    syncDesign();
    if (typeof window !== "undefined") {
      window.addEventListener("storage", syncDesign);
      window.addEventListener("theme_settings_updated", syncDesign);
      return () => {
        window.removeEventListener("storage", syncDesign);
        window.removeEventListener("theme_settings_updated", syncDesign);
      };
    }
  }, [variant]);

  if (!products || products.length === 0) return null;

  const cleanKey = String(activeVariant || "door_hanger")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_");

  if (AI_RECOMMENDER_VARIANTS[cleanKey]) {
    const DedicatedSection = AI_RECOMMENDER_VARIANTS[cleanKey];
    return (
      <div
        onClickCapture={(e) => {
          const target = e.target as HTMLElement;
          const linkEl = target.closest("a[href*='/products/']") as HTMLAnchorElement | null;
          if (linkEl) {
            const matchedProduct = products.find(
              (p: any) =>
                linkEl.href.includes(String(p.slug)) || linkEl.href.includes(String(p.id))
            );
            if (matchedProduct?.id) {
              recordAiProductClick(matchedProduct.id, subdomain);
            }
          }
        }}
      >
        <DedicatedSection
          products={products}
          title={title}
          subtitle={subtitle}
          primaryColor={primaryColor}
          subdomain={subdomain}
        />
      </div>
    );
  }

  const CardComponent = resolveProductCardComponent(cleanKey);

  const handleScroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const scrollAmount = 320;
      scrollRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
      <div className="flex items-center justify-between mb-8 pb-3 border-b border-slate-100">
        <div>
          <span
            style={{ color: primaryColor }}
            className="text-xs font-black uppercase tracking-wider flex items-center gap-1.5 font-mono"
          >
            <Sparkles className="w-3.5 h-3.5 animate-pulse" /> AI SMART RECOMMENDATIONS
          </span>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight mt-1">
            {title}
          </h2>
          {subtitle && (
            <p className="text-xs sm:text-sm text-slate-500 font-medium mt-1">
              {subtitle}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Link
            href="/products"
            className="text-xs font-bold text-slate-600 hover:text-emerald-600 transition-colors hidden sm:flex items-center gap-1 mr-2"
          >
            <span>View All</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => handleScroll("left")}
              className="w-8 h-8 rounded-full border-2 border-slate-200 bg-white hover:bg-slate-50 text-slate-700 flex items-center justify-center shadow-xs transition-all active:scale-95 cursor-pointer"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => handleScroll("right")}
              className="w-8 h-8 rounded-full border-2 border-slate-200 bg-white hover:bg-slate-50 text-slate-700 flex items-center justify-center shadow-xs transition-all active:scale-95 cursor-pointer"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex items-stretch gap-5 overflow-x-auto pt-4 pb-6 px-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden scroll-smooth"
      >
        {products.map((item: any, idx: number) => (
          <div
            key={item.id || idx}
            onClick={() => recordAiProductClick(item.id, subdomain)}
            className="w-64 sm:w-72 shrink-0 flex"
          >
            <CardComponent
              product={item}
              index={idx}
              subdomain={subdomain}
              primaryColor={primaryColor}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

export { PRODUCT_CARD_COMPONENTS };